<template>
  <div class="relative overflow-x-hidden flex-1 px-6 py-6 flex">
    <div class="flex-1">
      <!-- Title Bar -->
      <div
        class="flex items-center justify-between pb-4 border-b border-gray-300"
      >
        <div class="flex items-center space-x-6">
          <h1 class="text-2xl font-semibold text-gray-800">
            สรุปปริมาณการใช้งาน
          </h1>
        </div>

        <div class="flex items-center space-x-3">
          <DateInput
            v-model="startDate"
            readonly
            :disabledDateFn="notAfterEndDate"
          />

          <span class="text-base font-medium text-gray-800">ถึง</span>

          <DateInput
            v-model="endDate"
            readonly
            :disabledDateFn="notBeforeStartDate"
          />

          <button
            :disabled="loading"
            :class="{ 'cursor-not-allowed': loading }"
            @click="handleGetCustomerUsageReportSummary"
            type="button"
            class="
              mt-1
              inline-flex
              items-center
              px-4
              py-2
              border border-transparent
              rounded-md
              shadow-sm
              text-base
              font-semibold
              tracking-wider
              text-white
              bg-cyan-600
              hover:bg-cyan-700
              focus:outline-none
              focus:ring-2
              focus:ring-offset-2
              focus:ring-cyan-500
            "
          >
            เรียกดูข้อมูล
          </button>

          <button
            :disabled="errors.length > 0"
            @click="handleDownloadExcelCustomerUsageReportSummary"
            type="button"
            class="
              mt-1
              inline-flex
              items-center
              px-4
              py-2
              border border-gray-300
              rounded-md
              shadow-sm
              text-base
              font-medium
              text-gray-700
              bg-white
              hover:bg-gray-50
              focus:outline-none
              focus:ring-2
              focus:ring-offset-2
              focus:ring-cyan-500
            "
            :class="{ 'cursor-not-allowed': errors.length > 0 }"
          >
            Download Excel
          </button>
        </div>
      </div>
      <!-- end title bar -->

      <!-- Error Message -->
      <div
        v-if="errors.length > 0"
        class="px-4 py-3 bg-red-600 bg-opacity-20 flex items-center"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5 text-red-600"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
            clip-rule="evenodd"
          />
        </svg>
        <p class="text-base font-semibold text-red-800 flex-1 px-3">
          {{ errors[0] }}
        </p>
        <button @click="errors = []" type="button" class="focus:outline-none">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 text-red-600"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>
      <!-- End Error Message -->

      <!-- table -->
      <div class="mt-4">
        <AppDataTable
          :isCheckbox="false"
          :options="options"
          :headers="[
            {
              text: 'วันที่ (วัน / เดือน / ปี)',
              class: 'text-left',
              sortable: false,
              value: 'date',
            },
            {
              text: 'จำนวนคนที่ใช้งาน (คน)',
              class: 'text-left',
              sortable: false,
              value: 'usage',
            },
          ]"
          :items="filterCustomerUsageReportSummary.items"
          :loading="loading"
        >
          <template v-slot:date="{ item }">
            {{ item.date | formatDate }}
          </template>
          <template v-slot:usage="{ item }">
            {{ item.usage | formatNumber }}
          </template>
          <template
            v-slot:tfoot
            v-if="filterCustomerUsageReportSummary.items.length > 0"
          >
            <tr class="bg-white">
              <th
                class="text-right px-4 py-2 text-xl font-semibold text-gray-800"
              >
                รวม
              </th>
              <th
                class="text-left px-4 py-2 text-xl font-semibold text-gray-800"
              >
                {{ totalUsage | formatNumber }}
              </th>
            </tr>
          </template>
        </AppDataTable>
      </div>
      <!-- end table -->
    </div>

    <AppDownloadProgressModal
      :show="isDownloadProgressModal"
      :value="Math.ceil(downloadProgressValue)"
    />
  </div>
</template>

<script>
import moment from 'moment'
import numeral from 'numeral'
import _ from 'lodash'

import DateInput from '@/components/DateInput'
import AppDataTable from '@/components/AppDataTable'
import AppDownloadProgressModal from '@/components/AppDownloadProgressModal'

export default {
  name: 'CustomerUsageReportSummary',

  components: {
    DateInput,
    AppDataTable,
    AppDownloadProgressModal,
  },

  data() {
    return {
      isDownloadProgressModal: false,
      downloadProgressValue: 0,
      startDate: '',
      endDate: '',
      errors: [],
      loading: false,
      options: {
        sortBy: 'date',
        sortDesc: false,
      },
      totalUsage: 0,
      interval: null,
      timeout: null,
    }
  },

  filters: {
    formatDate: function (value) {
      return moment.utc(value).format('DD/MM/YYYY')
    },

    formatNumber: function (value) {
      return numeral(value).format('0,0')
    },
  },

  computed: {
    customerUsageReportSummary() {
      return this.$store.state.customer.customerUsageReportSummary
    },

    filterCustomerUsageReportSummary() {
      const { sortBy, sortDesc } = this.options

      let items = this.customerUsageReportSummary.map((element) => ({
        date: moment.utc(element.date).format(),
        usage: element.usage,
      }))

      items = items.sort((a, b) => {
        const sortA = a[sortBy] || ''
        const sortB = b[sortBy] || ''

        if (sortDesc) {
          if (sortA < sortB) return 1
          if (sortA > sortB) return -1
          return 0
        } else {
          if (sortA < sortB) return -1
          if (sortA > sortB) return 1
          return 0
        }
      })

      return { items }
    },
  },

  async created() {
    this.$store.commit('setMenusCustomerActive', true)
    this.$store.commit('customer/setCustomerUsageReportSummary', [])

    const uuid = this.$route.params.uuid
    const customer = await this.$store.dispatch('customer/fetchCustomer', uuid)

    this.$store.commit('setBreadcrumbs', [
      { text: 'รายชื่อลูกค้า', routeName: 'CustomerList' },
      {
        text: customer.title_th || 'ลูกค้า',
        routeName: 'CustomerDetails',
      },
      { text: 'สรุปปริมาณการใช้งาน', routeName: 'CustomerReportUse' },
    ])

    this.startDate = moment().startOf('month').format('DD/MM/YYYY')
    this.endDate = moment().format('DD/MM/YYYY')
  },

  methods: {
    handleGetCustomerUsageReportSummary() {
      this.errors = []
      this.fetchDataFromApi()
    },

    handleDownloadExcelCustomerUsageReportSummary() {
      this.errors = []
      this.downloadExcelCustomerUsageReportSummary()
    },

    async fetchDataFromApi() {
      try {
        this.loading = true

        const payload = {
          uuid: this.$route.params.uuid,
          startDate: moment(this.startDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
          endDate: moment(this.endDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
        }

        await this.$store.dispatch(
          'customer/fetchCustomerUsageReportSummary',
          payload
        )

        this.totalUsage = _.sumBy(this.customerUsageReportSummary, 'usage')
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors.push(error.response.data.Message)
        }
        console.log(error.response)
      } finally {
        this.loading = false
      }
    },

    generateFileProgress(job_process_uuid, ping_after_ms) {
      this.interval = setInterval(async () => {
        try {
          const response = await this.$store.dispatch(
            'customer/generateFileProgress',
            job_process_uuid
          )

          if (response.is_success) {
            this.downloadProgressValue = 100
            this.timeout = setTimeout(() => {
              this.isDownloadProgressModal = false
            }, 200)

            const fileUrl = response.resource_uri
            const fileName = fileUrl.split('/').pop()

            const a = document.createElement('a')
            a.setAttribute('hidden', '')
            a.setAttribute('href', fileUrl)
            a.setAttribute('download', fileName)
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)

            clearInterval(this.interval)
          } else {
            this.downloadProgressValue =
              (100 / response.total) * response.progress
          }
        } catch (error) {
          this.isDownloadProgressModal = false

          if (error.response && error.response.data) {
            this.errors.push(error.response.data.message)
          }
          console.log(error.response)

          clearInterval(this.interval)
        }
      }, ping_after_ms)
    },

    async downloadExcelCustomerUsageReportSummary() {
      try {
        this.downloadProgressValue = 0
        this.isDownloadProgressModal = true

        const payload = {
          uuid: this.$route.params.uuid,
          body: {
            start_date: moment(this.startDate, 'DD/MM/YYYY').format(
              'YYYY-MM-DD'
            ),
            end_date: moment(this.endDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
          },
        }

        const response = await this.$store.dispatch(
          'customer/generateFile',
          payload
        )

        this.generateFileProgress(
          response.job_process_uuid,
          response.ping_after_ms
        )
      } catch (error) {
        this.isDownloadProgressModal = false

        if (error.response && error.response.data) {
          this.errors.push(error.response.data.message)
        }
        console.log(error.response)
      }
    },

    notAfterEndDate(date) {
      return date > new Date(moment.utc(this.endDate, 'DD/MM/YYYY').format())
    },

    notBeforeStartDate(date) {
      return (
        date <
        new Date(
          moment.utc(this.startDate, 'DD/MM/YYYY').subtract(1, 'days').format()
        )
      )
    },
  },

  beforeDestroy() {
    clearTimeout(this.timeout)
  },
}
</script>
